import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../store";
import { Upload, Check } from "lucide-react";

export default function SiteSettingsPanel() {
  const { API, authHeaders, refreshSettings } = useApp();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setForm(r.data));
  }, [API]);

  if (!form) return <p>Loading settings…</p>;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadImg = async (key, file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await axios.post(`${API}/upload`, fd, { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } });
    set(key, `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, form, { headers: authHeaders });
      await refreshSettings();
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-12" data-testid="site-settings-panel">
      <Group title="Brand">
        <Row label="Brand name (top line)"><Inp v={form.brand_name_top} on={v => set("brand_name_top", v)} /></Row>
        <Row label="Brand name (bottom line)"><Inp v={form.brand_name_bottom} on={v => set("brand_name_bottom", v)} /></Row>
        <Row label="Brand tagline"><Inp v={form.brand_tagline} on={v => set("brand_tagline", v)} /></Row>
      </Group>

      <Group title="Theme Colors">
        <Row label="Accent color (copper)"><Color v={form.accent_color} on={v => set("accent_color", v)} /></Row>
        <Row label="Primary text"><Color v={form.text_primary} on={v => set("text_primary", v)} /></Row>
        <Row label="Background"><Color v={form.bg_primary} on={v => set("bg_primary", v)} /></Row>
        <Row label="Background (sand)"><Color v={form.bg_secondary} on={v => set("bg_secondary", v)} /></Row>
      </Group>

      <Group title="Hero Section">
        <Row label="Eyebrow"><Inp v={form.hero_eyebrow} on={v => set("hero_eyebrow", v)} /></Row>
        <Row label="Headline — top"><Inp v={form.hero_headline_top} on={v => set("hero_headline_top", v)} /></Row>
        <Row label="Headline — bottom (italic)"><Inp v={form.hero_headline_bottom} on={v => set("hero_headline_bottom", v)} /></Row>
        <Row label="Sub-headline"><Area v={form.hero_subheadline} on={v => set("hero_subheadline", v)} /></Row>
        <Row label="Hero video (MP4, autoplay & muted)">
          <VideoField url={form.hero_video_url} onUrl={v => set("hero_video_url", v)} onFile={f => uploadImg("hero_video_url", f)} />
        </Row>
        <Row label="Hero quote"><Inp v={form.hero_quote} on={v => set("hero_quote", v)} /></Row>
      </Group>

      <Group title="Story Section">
        <Row label="Eyebrow"><Inp v={form.story_eyebrow} on={v => set("story_eyebrow", v)} /></Row>
        <Row label="Headline — top"><Inp v={form.story_headline_top} on={v => set("story_headline_top", v)} /></Row>
        <Row label="Headline — bottom (italic)"><Inp v={form.story_headline_bottom} on={v => set("story_headline_bottom", v)} /></Row>
        <Row label="Paragraph 1"><Area v={form.story_paragraph_1} on={v => set("story_paragraph_1", v)} /></Row>
        <Row label="Paragraph 2"><Area v={form.story_paragraph_2} on={v => set("story_paragraph_2", v)} /></Row>
        <Row label="Story image">
          <ImgField url={form.story_image_url} onUrl={v => set("story_image_url", v)} onFile={f => uploadImg("story_image_url", f)} />
        </Row>
      </Group>

      <Group title="Homepage Sections (Show / Hide)">
        <div className="grid sm:grid-cols-2 gap-3">
          <Toggle label="Marquee ribbon" v={form.show_marquee} on={v => set("show_marquee", v)} />
          <Toggle label="Category bento" v={form.show_categories} on={v => set("show_categories", v)} />
          <Toggle label="Featured products" v={form.show_featured} on={v => set("show_featured", v)} />
          <Toggle label="Story block" v={form.show_story} on={v => set("show_story", v)} />
          <Toggle label="B2B CTA" v={form.show_b2b_cta} on={v => set("show_b2b_cta", v)} />
        </div>
      </Group>

      <Group title="Category Tiles (home 'Categories of essence' section)">
        <div className="grid md:grid-cols-2 gap-8">
          <CategoryTileEditor
            heading="Floral & Absolute"
            img={form.cat_floral_image} onImg={v => set("cat_floral_image", v)}
            sub={form.cat_floral_subtitle} onSub={v => set("cat_floral_subtitle", v)}
            onFile={f => uploadImg("cat_floral_image", f)}
          />
          <CategoryTileEditor
            heading="Attar"
            img={form.cat_attar_image} onImg={v => set("cat_attar_image", v)}
            sub={form.cat_attar_subtitle} onSub={v => set("cat_attar_subtitle", v)}
            onFile={f => uploadImg("cat_attar_image", f)}
          />
          <CategoryTileEditor
            heading="Wood & Resin"
            img={form.cat_wood_image} onImg={v => set("cat_wood_image", v)}
            sub={form.cat_wood_subtitle} onSub={v => set("cat_wood_subtitle", v)}
            onFile={f => uploadImg("cat_wood_image", f)}
          />
          <CategoryTileEditor
            heading="Herb & Grass"
            img={form.cat_herb_image} onImg={v => set("cat_herb_image", v)}
            sub={form.cat_herb_subtitle} onSub={v => set("cat_herb_subtitle", v)}
            onFile={f => uploadImg("cat_herb_image", f)}
          />
        </div>
      </Group>

      <Group title="Footer & Contact">
        <Row label="About text"><Area v={form.footer_about} on={v => set("footer_about", v)} /></Row>
        <Row label="Location"><Inp v={form.footer_contact_location} on={v => set("footer_contact_location", v)} /></Row>
        <Row label="Email"><Inp v={form.footer_contact_email} on={v => set("footer_contact_email", v)} /></Row>
        <Row label="Phone"><Inp v={form.footer_contact_phone} on={v => set("footer_contact_phone", v)} /></Row>
      </Group>

      <Group title="Commerce — Tax & Shipping">
        <Row label="GST rate (decimal, e.g. 0.18 for 18%)"><Inp type="number" step="0.01" v={form.gst_rate} on={v => set("gst_rate", parseFloat(v))} /></Row>
        <Row label="Free shipping threshold (₹)"><Inp type="number" v={form.free_shipping_threshold} on={v => set("free_shipping_threshold", parseFloat(v))} /></Row>
        <Row label="Flat shipping rate (₹)"><Inp type="number" v={form.shipping_flat_rate} on={v => set("shipping_flat_rate", parseFloat(v))} /></Row>
        <Row label="Currency symbol"><Inp v={form.currency_symbol} on={v => set("currency_symbol", v)} /></Row>
      </Group>

      <Group title="WhatsApp Order Notifications">
        <Row label="Your WhatsApp number (with country code)">
          <Inp v={form.owner_whatsapp} on={v => set("owner_whatsapp", v)} />
        </Row>
        <p className="text-xs text-[#8A8D8A] leading-relaxed">
          When set, customers see a "Send Order via WhatsApp" button after checkout. Tapping it opens WhatsApp on their phone with the order details pre-filled in a message addressed to <b>this number</b>. They just tap "Send" — message arrives on your WhatsApp instantly. <br/>
          Format: <code className="text-copper">+919876543210</code> (include country code, no spaces or dashes). Leave empty to hide the button.
        </p>
      </Group>

      <Group title="Payment (UPI · Bank · QR)">
        <p className="text-xs text-[#8A8D8A] leading-relaxed mb-3">
          Customers see these at checkout after entering their address. They pay you directly (no gateway fee),
          then enter the UTR / transaction reference which you verify manually in Admin → Orders.
        </p>
        <Row label="Payment QR image (upload)">
          <div>
            <ImgField
              url={form.payment_qr_url}
              onUrl={v => set("payment_qr_url", v)}
              onFile={f => uploadImg("payment_qr_url", f)}
            />
            <p className="text-[10px] text-[#8A8D8A] mt-1">Upload your UPI / bank QR code image. Ideally a square PNG or JPG.</p>
          </div>
        </Row>
        <Row label="UPI ID"><Inp v={form.payment_upi_id} on={v => set("payment_upi_id", v)} /></Row>
        <Row label="Bank name"><Inp v={form.payment_bank_name} on={v => set("payment_bank_name", v)} /></Row>
        <Row label="Account holder name"><Inp v={form.payment_bank_account_name} on={v => set("payment_bank_account_name", v)} /></Row>
        <Row label="Account number"><Inp v={form.payment_bank_account_number} on={v => set("payment_bank_account_number", v)} /></Row>
        <Row label="IFSC code"><Inp v={form.payment_bank_ifsc} on={v => set("payment_bank_ifsc", v)} /></Row>
        <Row label="Instructions (shown to customer at checkout)">
          <Area v={form.payment_instructions} on={v => set("payment_instructions", v)} />
        </Row>
      </Group>

      <Group title="Launch Discount Coupon">
        <div className="grid sm:grid-cols-3 gap-4">
          <Row label="Code"><Inp v={form.coupon_code} on={v => set("coupon_code", v.toUpperCase())} /></Row>
          <Row label="Discount %"><Inp type="number" step="1" v={form.coupon_percent} on={v => set("coupon_percent", parseFloat(v))} /></Row>
          <Toggle label="Active" v={form.coupon_active} on={v => set("coupon_active", v)} />
        </div>
        <Row label="Marketing label (shown above coupon input at checkout)">
          <Inp v={form.coupon_label} on={v => set("coupon_label", v)} />
        </Row>
        <p className="text-xs text-[#8A8D8A]">
          One configurable discount code applied to subtotal. Default: <b>LAUNCH10</b> = 10% off. Toggle off to disable instantly. Discount applies before GST.
        </p>
      </Group>

      <FilePersistencePanel />

      <div className="sticky bottom-4 z-10 bg-[#FAF9F6] border-t border-[#E5E1D8] pt-4 flex items-center gap-4">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-settings">
          {saving ? "Saving…" : "Save all changes"}
        </button>
        {saved && (
          <span className="text-xs text-[#3C6348] flex items-center gap-1" data-testid="settings-saved">
            <Check size={14} /> Saved — refresh the home page to see updates
          </span>
        )}
      </div>
    </div>
  );
}

const Group = ({ title, children }) => (
  <div className="border-l-2 border-copper pl-6">
    <h3 className="font-serif-display text-2xl mb-6">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

function FilePersistencePanel() {
  const { API, authHeaders } = useApp();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const load = () => axios.get(`${API}/admin/files/status`, { headers: authHeaders }).then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [API, authHeaders]);

  const sync = async () => {
    setBusy(true); setSyncMsg("");
    try {
      const r = await axios.post(`${API}/admin/files/sync`, {}, { headers: authHeaders });
      const m = r.data.mirrored, rs = r.data.restored;
      setSyncMsg(`Backed up ${m.mirrored} new file${m.mirrored !== 1 ? "s" : ""}; restored ${rs.restored} from backup.`);
      await load();
    } catch (e) {
      setSyncMsg("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const safe = status.disk_only.length === 0;

  return (
    <Group title="File Persistence (uploads backup)">
      <p className="text-xs text-[#8A8D8A] leading-relaxed">
        Every upload (product images, COA / GC-MS PDFs, hero video) is now automatically backed up into MongoDB.
        When you redeploy, missing files are restored automatically — no more wiped images.
      </p>
      <div className="grid grid-cols-2 gap-4 text-sm" data-testid="file-persistence-stats">
        <div className="border border-[#E5E1D8] p-4">
          <div className="overline text-[10px] text-[#8A8D8A] mb-1">On disk</div>
          <div className="font-serif-display text-2xl">{status.disk_count}</div>
        </div>
        <div className="border border-[#E5E1D8] p-4">
          <div className="overline text-[10px] text-[#8A8D8A] mb-1">Backed up in DB</div>
          <div className="font-serif-display text-2xl">{status.gridfs_count}</div>
        </div>
      </div>
      {!safe && (
        <p className="text-xs text-[#9E3C3C]" data-testid="file-persistence-warning">
          ⚠ {status.disk_only.length} file{status.disk_only.length !== 1 ? "s" : ""} on disk are not yet backed up. Click <b>Force sync now</b> to back them up.
        </p>
      )}
      {safe && (
        <p className="text-xs text-[#3C6348]" data-testid="file-persistence-safe">✓ All files backed up — safe to redeploy.</p>
      )}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={sync} disabled={busy} className="btn-ghost text-xs" data-testid="file-persistence-sync">
          {busy ? "Syncing…" : "Force sync now"}
        </button>
        {syncMsg && <span className="text-xs text-[#5C5E5C]">{syncMsg}</span>}
      </div>
    </Group>
  );
}

const Row = ({ label, children }) => (
  <div className="grid md:grid-cols-3 gap-4 items-start py-2">
    <label className="overline text-[10px] text-[#5C5E5C] pt-2">{label}</label>
    <div className="md:col-span-2">{children}</div>
  </div>
);
const Inp = ({ v, on, type = "text", step }) => (
  <input type={type} step={step} value={v ?? ""} onChange={e => on(e.target.value)} className="input-underline" />
);
const Area = ({ v, on }) => (
  <textarea value={v ?? ""} onChange={e => on(e.target.value)} rows={3} className="input-underline resize-none" />
);
const Color = ({ v, on }) => (
  <div className="flex items-center gap-3">
    <input type="color" value={v || "#000000"} onChange={e => on(e.target.value)} className="w-12 h-10 border border-[#E5E1D8] cursor-pointer" />
    <input type="text" value={v ?? ""} onChange={e => on(e.target.value)} className="input-underline font-mono" />
  </div>
);
const Toggle = ({ label, v, on }) => (
  <label className="flex items-center gap-3 cursor-pointer py-2">
    <input type="checkbox" checked={!!v} onChange={e => on(e.target.checked)} className="w-4 h-4" />
    <span className="text-sm">{label}</span>
  </label>
);

const CategoryTileEditor = ({ heading, img, onImg, sub, onSub, onFile }) => (
  <div className="border border-[#E5E1D8] p-4 space-y-3" data-testid={`cat-edit-${heading.toLowerCase().replace(/[^a-z]/g, '-')}`}>
    <div className="overline text-[10px] text-copper">{heading}</div>
    {img && (
      <div className="relative aspect-[3/4] overflow-hidden">
        <img src={img} alt={heading} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-3 text-[#FAF9F6]">
          <div className="text-[9px] uppercase tracking-widest text-[#EBE4D8] mb-1">{sub || "—"}</div>
          <div className="font-serif-display text-lg">{heading}</div>
        </div>
      </div>
    )}
    <div>
      <label className="overline text-[10px] block mb-1">Image URL</label>
      <div className="flex gap-2 items-start">
        <input type="url" value={img ?? ""} onChange={e => onImg(e.target.value)} className="input-underline flex-1" placeholder="Paste URL" />
        <label className="border border-dashed border-[#E5E1D8] hover:border-copper px-2 py-1.5 text-[10px] cursor-pointer flex items-center gap-1 whitespace-nowrap">
          <Upload size={11} /> Upload
          <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} className="hidden" />
        </label>
      </div>
    </div>
    <div>
      <label className="overline text-[10px] block mb-1">Subtitle (small text on tile)</label>
      <input type="text" value={sub ?? ""} onChange={e => onSub(e.target.value)} className="input-underline" />
    </div>
  </div>
);
const ImgField = ({ url, onUrl, onFile }) => (
  <div className="space-y-3">
    <div className="flex gap-3 items-start">
      <input type="url" value={url ?? ""} onChange={e => onUrl(e.target.value)} className="input-underline flex-1" placeholder="Image URL" />
      <label className="border border-dashed border-[#E5E1D8] hover:border-copper px-3 py-2 text-xs cursor-pointer flex items-center gap-2 whitespace-nowrap">
        <Upload size={12} /> Upload
        <input type="file" accept="image/*" onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} className="hidden" />
      </label>
    </div>
    {url && <img src={url} alt="preview" className="w-32 h-24 object-cover" />}
  </div>
);

const VideoField = ({ url, onUrl, onFile }) => {
  const [warning, setWarning] = React.useState("");
  return (
    <div className="space-y-3">
      <div className="flex gap-3 items-start">
        <input type="url" value={url ?? ""} onChange={e => onUrl(e.target.value)} className="input-underline flex-1" placeholder="Video URL (.mp4) — leave empty to use hero image" />
        <label className="border border-dashed border-[#E5E1D8] hover:border-copper px-3 py-2 text-xs cursor-pointer flex items-center gap-2 whitespace-nowrap">
          <Upload size={12} /> Upload .mp4
          <input type="file" accept="video/mp4,video/*" onChange={async e => {
            const f = e.target.files?.[0]; if (!f) return;
            setWarning("");
            if (f.name.toLowerCase().endsWith(".mov")) {
              setWarning("⚠️ .mov files DO NOT play in Chrome, Firefox or Edge — most of your visitors will see nothing. Convert to .mp4 first (cloudconvert.com or QuickTime → Export As → 1080p) and re-upload.");
              return;
            }
            await onFile(f);
          }} className="hidden" />
        </label>
      </div>
      {warning && <p className="text-xs text-[#9E3C3C] leading-relaxed bg-[#9E3C3C]/10 p-3" data-testid="video-warning">{warning}</p>}
      {url && (
        <video src={url} autoPlay muted loop playsInline className="w-48 h-32 object-cover" />
      )}
      {url && (
        <button type="button" onClick={() => onUrl("")} className="text-[10px] text-[#9E3C3C] underline">Remove video (revert to image)</button>
      )}
      <p className="text-[10px] text-[#8A8D8A]">Tip: use .mp4 (H.264) for universal browser support. Keep under 15 MB for fastest load. <b>iPhone .mov uploads must be converted to .mp4 first.</b></p>
    </div>
  );
};
